import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Sparkles, Check, CircleDot } from 'lucide-react';
import { useClickSound } from '../hooks/useClickSound';
import { TOOL_COSTS } from '../hooks/useEnergyManager';
interface ChakraCleansingProps {
  onClose: () => void;
  onComplete: () => void;
}
type Phase = 'selection' | 'breathing' | 'outro';
type BreathPhase = 'wdech' | 'wstrzymaj' | 'wydech' | 'wstrzymaj2';
const CHAKRAS = [{
  id: 'root',
  icon: '🔴',
  name: 'Czakra Korzenia',
  sanskrit: 'Muladhara',
  intent: 'Uziemienie i bezpieczeństwo',
  desc: 'Odpowiada za uziemienie, poczucie bezpieczeństwa, przetrwanie i stabilność życiową. Oczyść lęk o przyszłość.',
  freq: 396,
  color: 'from-red-500 to-red-700',
  shadow: 'shadow-red-500/30'
}, {
  id: 'sacral',
  icon: '🟠',
  name: 'Czakra Sakralna',
  sanskrit: 'Svadhisthana',
  intent: 'Kreatywność i emocje',
  desc: 'Centrum kreatywności, radości, emocji i przyjemności. Oczyść zablokowane emocje i poczucie winy.',
  freq: 417,
  color: 'from-orange-400 to-orange-600',
  shadow: 'shadow-orange-500/30'
}, {
  id: 'solar',
  icon: '🟡',
  name: 'Splot Słoneczny',
  sanskrit: 'Manipura',
  intent: 'Siła woli i pewność siebie',
  desc: 'Twoja siła woli, pewność siebie, motywacja i sprawczość. Oczyść uczucie bezsilności i wstydu.',
  freq: 528,
  color: 'from-yellow-400 to-yellow-600',
  shadow: 'shadow-yellow-500/30'
}, {
  id: 'heart',
  icon: '🟢',
  name: 'Czakra Serca',
  sanskrit: 'Anahata',
  intent: 'Miłość i przebaczenie',
  desc: 'Bezwarunkowa miłość, empatia, relacje i przebaczenie. Oczyść żal, smutek i poczucie odrzucenia.',
  freq: 639,
  color: 'from-emerald-400 to-emerald-600',
  shadow: 'shadow-emerald-500/30'
}, {
  id: 'throat',
  icon: '🔵',
  name: 'Czakra Gardła',
  sanskrit: 'Vishuddha',
  intent: 'Wyrażenie siebie',
  desc: 'Komunikacja, autentyczność, wyrażanie swojej prawdy. Oczyść niewypowiedziane słowa i lęk przed oceną.',
  freq: 741,
  color: 'from-blue-400 to-blue-600',
  shadow: 'shadow-blue-500/30'
}, {
  id: 'third-eye',
  icon: '🔮',
  name: 'Trzecie Oko',
  sanskrit: 'Ajna',
  intent: 'Intuicja i mądrość',
  desc: 'Intuicja, wyobraźnia, mądrość, jasność widzenia. Oczyść iluzje, zagubienie i "mgłę mózgową".',
  freq: 852,
  color: 'from-indigo-400 to-indigo-600',
  shadow: 'shadow-indigo-500/30'
}, {
  id: 'crown',
  icon: '👑',
  name: 'Czakra Korony',
  sanskrit: 'Sahasrara',
  intent: 'Połączenie ze źródłem',
  desc: 'Połączenie z wszechświatem, duchowość, poszerzona świadomość. Oczyść poczucie izolacji i braku sensu.',
  freq: 963,
  color: 'from-purple-400 to-purple-600',
  shadow: 'shadow-purple-500/30'
}];
export function ChakraCleansing({
  onClose,
  onComplete
}: ChakraCleansingProps) {
  const [phase, setPhase] = useState<Phase>('selection');
  const [selectedChakra, setSelectedChakra] = useState<(typeof CHAKRAS)[0] | null>(null);
  // Breathing state
  const [breathPhase, setBreathPhase] = useState<BreathPhase>('wdech');
  const [timeLeft, setTimeLeft] = useState(4);
  const [cycle, setCycle] = useState(1);
  const [isActive, setIsActive] = useState(false);
  const TOTAL_CYCLES = 8; // ~2 minutes (8 * 16s = 128s)
  const {
    playClick,
    playSuccess,
    playBowl
  } = useClickSound();
  const audioCtxRef = useRef<AudioContext | null>(null);
  const oscRef = useRef<OscillatorNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  // Speech synthesis
  const speak = useCallback((text: string) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'pl-PL';
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const polishVoice = voices.find(v => v.lang.startsWith('pl'));
    if (polishVoice) utterance.voice = polishVoice;
    window.speechSynthesis.speak(utterance);
  }, []);
  // Audio cleanup
  useEffect(() => {
    return () => {
      if (oscRef.current) {
        try {
          oscRef.current.stop();
        } catch (e) {}
      }
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close();
      }
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);
  // Breathing Engine
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (isActive && phase === 'breathing') {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setBreathPhase(currentPhase => {
              switch (currentPhase) {
                case 'wdech':
                  speak('Wstrzymaj');
                  return 'wstrzymaj';
                case 'wstrzymaj':
                  speak('Wydech');
                  return 'wydech';
                case 'wydech':
                  speak('Wstrzymaj');
                  return 'wstrzymaj2';
                case 'wstrzymaj2':
                  if (cycle >= TOTAL_CYCLES) {
                    setIsActive(false);
                    setPhase('outro');
                    playSuccess();
                    if (oscRef.current && gainRef.current && audioCtxRef.current) {
                      gainRef.current.gain.linearRampToValueAtTime(0, audioCtxRef.current.currentTime + 2);
                      setTimeout(() => {
                        try {
                          oscRef.current?.stop();
                        } catch (e) {}
                      }, 2000);
                    }
                    if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
                    return 'wstrzymaj2';
                  }
                  setCycle(c => c + 1);
                  speak('Wdech');
                  return 'wdech';
              }
            });
            return 4;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isActive, phase, cycle, speak, playSuccess]);
  const handleSelectChakra = (chakra: (typeof CHAKRAS)[0]) => {
    playClick();
    setSelectedChakra(chakra);
    setPhase('breathing');
    // Start Audio
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      audioCtxRef.current = new AudioContext();
      oscRef.current = audioCtxRef.current.createOscillator();
      gainRef.current = audioCtxRef.current.createGain();
      oscRef.current.type = 'sine';
      oscRef.current.frequency.value = chakra.freq;
      gainRef.current.gain.setValueAtTime(0, audioCtxRef.current.currentTime);
      gainRef.current.gain.linearRampToValueAtTime(0.1, audioCtxRef.current.currentTime + 2);
      oscRef.current.connect(gainRef.current);
      gainRef.current.connect(audioCtxRef.current.destination);
      oscRef.current.start();
    } catch (e) {
      console.error('Audio setup failed', e);
    }
    // Start breathing
    setTimeout(() => {
      setIsActive(true);
      speak('Wdech');
    }, 1000);
  };
  const handleFinish = () => {
    playClick();
    onComplete(10);
  };
  const getOrbAnimation = () => {
    if (!isActive) return {
      scale: 1,
      opacity: 0.5
    };
    switch (breathPhase) {
      case 'wdech':
        return {
          scale: 2,
          opacity: 0.8
        };
      case 'wydech':
        return {
          scale: 1,
          opacity: 0.5
        };
      case 'wstrzymaj':
      case 'wstrzymaj2':
        return {
          scale: breathPhase === 'wstrzymaj' ? [2, 2.05, 2] : [1, 1.05, 1],
          opacity: breathPhase === 'wstrzymaj' ? 0.8 : 0.5,
          transition: {
            repeat: Infinity,
            duration: 1
          }
        };
    }
  };
  const getPhaseText = () => {
    if (!isActive) return 'Przygotuj się...';
    switch (breathPhase) {
      case 'wdech':
        return 'Wdech...';
      case 'wstrzymaj':
        return 'Wstrzymaj...';
      case 'wydech':
        return 'Wydech...';
      case 'wstrzymaj2':
        return 'Wstrzymaj...';
    }
  };
  return <motion.div className="fixed inset-0 z-50 flex flex-col w-full h-full overflow-hidden bg-slate-900 text-white" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 20
  }} data-id="element-286">
      {/* Header */}
      <div className="w-full flex items-center justify-between p-4 z-20 shrink-0" data-id="element-287">
        <button onClick={() => {
        playClick();
        onClose();
      }} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors backdrop-blur-md border border-white/20" data-id="element-288">
          <ArrowLeft className="w-5 h-5" data-id="element-289" />
        </button>
        <div className="flex flex-col items-center" data-id="element-290">
          <span className="text-[10px] text-slate-400 font-bold tracking-widest uppercase" data-id="element-291">
            Oddech Pudełkowy
          </span>
          <span className="text-sm font-medium" data-id="element-292">Oczyszczanie Czakr</span>
        </div>
        <div className="w-10 h-10" data-id="element-293" />
      </div>

      <div className="flex-1 relative flex flex-col items-center justify-center p-6 overflow-y-auto" data-id="element-294">
        <AnimatePresence mode="wait" data-id="element-295">
          {/* SELECTION PHASE */}
          {phase === 'selection' && <motion.div key="selection" className="w-full max-w-md flex flex-col items-center pb-10" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          x: -20
        }} data-id="element-296">
              <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mb-6 border border-slate-700 shadow-lg" data-id="element-297">
                <CircleDot className="w-8 h-8 text-slate-300" data-id="element-298" />
              </div>
              <h1 className="text-2xl font-light mb-2 text-center" data-id="element-299">
                Wybierz intencję
              </h1>
              <p className="text-slate-400 text-sm mb-8 text-center max-w-xs" data-id="element-300">
                Wybierz obszar, który potrzebuje natychmiastowego uwolnienia.
              </p>

              <div className="w-full space-y-3" data-id="element-301">
                {CHAKRAS.map(chakra => <button key={chakra.id} onClick={() => handleSelectChakra(chakra)} className="w-full bg-slate-800 border border-slate-700 rounded-2xl p-4 flex items-center hover:bg-slate-700 transition-all group" data-id="element-302">
                    <div className="flex items-center space-x-4 flex-1 min-w-0" data-id="element-303">
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${chakra.color} flex items-center justify-center text-xl shadow-lg ${chakra.shadow} shrink-0`} data-id="element-304">
                        {chakra.icon}
                      </div>
                      <div className="text-left min-w-0" data-id="element-305">
                        <div className="flex items-center space-x-2 mb-0.5" data-id="element-306">
                          <h3 className="font-bold text-white text-sm" data-id="element-307">
                            {chakra.name}
                          </h3>
                          <span className="text-[10px] text-slate-500 italic" data-id="element-308">
                            {chakra.sanskrit}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-300 leading-snug line-clamp-2" data-id="element-309">
                          {chakra.desc}
                        </p>
                        <span className="text-[10px] font-bold text-slate-500 mt-1 inline-block" data-id="element-310">
                          {chakra.freq} Hz
                        </span>
                      </div>
                    </div>
                  </button>)}
              </div>
            </motion.div>}

          {/* BREATHING PHASE */}
          {phase === 'breathing' && selectedChakra && <motion.div key="breathing" className="flex flex-col items-center justify-center w-full h-full" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} data-id="element-311">
              <div className="absolute top-10 text-center" data-id="element-312">
                <h2 className="text-xl font-medium mb-1" data-id="element-313">
                  {selectedChakra.intent}
                </h2>
                <p className="text-xs text-slate-400 uppercase tracking-widest" data-id="element-314">
                  {selectedChakra.name} • {selectedChakra.freq} Hz
                </p>
                <div className="mt-4 text-[10px] font-bold tracking-widest uppercase opacity-50" data-id="element-315">
                  Cykl {Math.min(cycle, TOTAL_CYCLES)}/{TOTAL_CYCLES}
                </div>
              </div>

              <div className="relative w-full flex items-center justify-center h-64" data-id="element-316">
                <motion.div className={`absolute w-32 h-32 rounded-full bg-gradient-to-tr ${selectedChakra.color} blur-md`} animate={getOrbAnimation()} transition={breathPhase === 'wdech' || breathPhase === 'wydech' ? {
              duration: 4,
              ease: 'easeInOut'
            } : {
              duration: 1
            }} data-id="element-317" />
                <motion.div className={`absolute w-32 h-32 rounded-full bg-gradient-to-tr ${selectedChakra.color} opacity-50 blur-xl`} animate={getOrbAnimation()} transition={breathPhase === 'wdech' || breathPhase === 'wydech' ? {
              duration: 4,
              ease: 'easeInOut'
            } : {
              duration: 1
            }} data-id="element-318" />
              </div>

              <div className="z-10 text-center mt-12 h-24" data-id="element-319">
                <AnimatePresence mode="wait" data-id="element-320">
                  <motion.h2 key={getPhaseText()} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="text-3xl font-light mb-2" data-id="element-321">
                    {getPhaseText()}
                  </motion.h2>
                </AnimatePresence>
                {isActive && <div className="text-2xl font-medium opacity-70 tabular-nums" data-id="element-322">
                    {timeLeft}
                  </div>}
              </div>
            </motion.div>}

          {/* OUTRO PHASE */}
          {phase === 'outro' && <motion.div key="outro" className="flex flex-col items-center text-center max-w-sm" initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          type: 'spring',
          bounce: 0.4
        }} data-id="element-323">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 border-2 shadow-lg bg-gradient-to-br ${selectedChakra?.color} ${selectedChakra?.shadow}`} data-id="element-324">
                <Sparkles className="w-10 h-10 text-white" fill="currentColor" data-id="element-325" />
              </div>

              <h2 className="text-3xl font-light mb-2" data-id="element-326">
                Oczyszczanie zakończone
              </h2>
              <p className="text-slate-400 mb-8 text-sm" data-id="element-327">
                Twoja energia w obszarze {selectedChakra?.name} płynie teraz
                swobodnie.
              </p>

              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-4 flex flex-col items-center min-w-[140px] mb-10" data-id="element-328">
                <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 font-bold" data-id="element-329">
                  Koszt Energii
                </span>
                <span className="text-2xl font-bold text-yellow-500 flex items-center" data-id="element-330">
                  {TOOL_COSTS.chakraCleansing}{' '}
                  <Sparkles className="w-5 h-5 ml-1.5" fill="currentColor" data-id="element-331" />
                </span>
              </div>

              <button onClick={handleFinish} className="w-full bg-white text-slate-900 rounded-full py-4 text-sm font-bold hover:bg-slate-100 transition-colors shadow-lg" data-id="element-332">
                Odbierz i zakończ
              </button>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}